import React, { useEffect, useRef, useState } from 'react';
import { VideoBlockData } from '../../../../models/courses';
import {
  buildEmbedUrl,
  buildShareUrl,
  fetchThumbnail,
  parseVideoUrl,
} from './videoEmbed';
import styles from './VideoBlock.module.css';

interface VideoBlockProps {
  data: VideoBlockData;
  onChange: (_data: VideoBlockData) => void;
  disabled?: boolean;
}

const PlayIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5v14l11-7z" />
  </svg>
);

export const VideoBlock: React.FC<VideoBlockProps> = ({
  data,
  onChange,
  disabled,
}) => {
  const [urlInput, setUrlInput] = useState<string>(() =>
    buildShareUrl(data.provider, data.video_id),
  );
  const [thumbnail, setThumbnail] = useState<string | null>(null);
  const [loadingThumb, setLoadingThumb] = useState<boolean>(false);
  const [playing, setPlaying] = useState<boolean>(false);
  const posterFailed = useRef<boolean>(false);

  const { provider, video_id: videoId } = data;

  // Resolve the poster whenever the resolved video changes.
  useEffect(() => {
    setPlaying(false);
    posterFailed.current = false;

    if (!videoId) {
      setThumbnail(null);
      return;
    }

    let cancelled = false;
    setLoadingThumb(true);
    fetchThumbnail(provider, videoId)
      .then((url) => {
        if (!cancelled) {
          setThumbnail(url);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingThumb(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [provider, videoId]);

  const handleUrlChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setUrlInput(value);

    const parsed = parseVideoUrl(value);
    if (parsed) {
      onChange({ ...data, provider: parsed.provider, video_id: parsed.id });
    } else if (!value.trim()) {
      onChange({ ...data, video_id: '' });
    }
  };

  const isKnown = Boolean(videoId) && Boolean(parseVideoUrl(urlInput));
  const showUnknown = Boolean(urlInput.trim()) && !videoId;

  return (
    <div className={styles.wrap}>
      <div className={styles.field}>
        <span className={styles.label}>Ссылка на видео (Kinescope, YouTube)</span>
        <input
          className={styles.input}
          value={urlInput}
          onChange={handleUrlChange}
          disabled={disabled}
          placeholder="https://kinescope.io/9CEiffkWDVDojiJLZEt29X"
        />
      </div>

      {showUnknown && (
        <div className={styles.hint}>
          Не удалось распознать ссылку. Вставьте ссылку Kinescope или YouTube.
        </div>
      )}

      {isKnown && (
        <div className={styles.player}>
          {playing ? (
            <iframe
              className={styles.iframe}
              src={buildEmbedUrl(provider, videoId, true)}
              title="Видео"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media;"
              allowFullScreen
            />
          ) : (
            <button
              type="button"
              className={styles.poster}
              onClick={() => setPlaying(true)}
              aria-label="Смотреть видео"
            >
              {thumbnail ? (
                <img
                  className={styles.posterImg}
                  src={thumbnail}
                  alt=""
                  onError={() => setThumbnail(null)}
                />
              ) : (
                <span className={styles.posterFallback} />
              )}
              <span className={styles.scrim} />
              {loadingThumb ? (
                <span className={styles.spinner} />
              ) : (
                <span className={styles.playBtn}>
                  <PlayIcon />
                </span>
              )}
            </button>
          )}
        </div>
      )}

      <div className={styles.field}>
        <span className={styles.label}>Подпись</span>
        <input
          className={styles.input}
          value={data.caption}
          onChange={(e) => onChange({ ...data, caption: e.target.value })}
          disabled={disabled}
          placeholder="Необязательно"
        />
      </div>
    </div>
  );
};
